/**
 * Single source of truth for the 8 petty-cash `expense_category` values.
 *
 * The enum mirrors `cash_movements.expense_category` (closed for V1 per
 * ADR-0012 §10 Q7). Icons come from `prototype/cash/petty-cash-expense.html`
 * lines ~1056–1166 — the `.cat-grid` quick-add tiles — so the React grid
 * matches the design contract byte-for-byte.
 *
 * Why `ReactNode` and not a `LucideIcon`? Lucide doesn't ship 1:1
 * matches for the prototype's drinks-cup / printer / spanner glyphs;
 * inlining the prototype's SVG paths keeps both surfaces in lockstep.
 *
 * @see prototype/cash/petty-cash-expense.html `.cat-grid`
 * @see packages/db/src/schema/cash-movements.ts — `expense_category` enum
 */
import type { ReactNode } from "react";

export const PETTY_CASH_CATEGORIES = [
  "postage",
  "conveyance",
  "travel",
  "refreshments",
  "office_supplies",
  "printing_stationery",
  "repairs_maintenance",
  "sundries",
] as const;

export type PettyCashCategory = (typeof PETTY_CASH_CATEGORIES)[number];

export interface CategoryMeta {
  /** Human label as it appears on the tile (matches prototype copy). */
  label: string;
  /** Inline SVG body — paths only. The grid sizes the wrapping `<svg>`. */
  icon: ReactNode;
}

export const CATEGORY_META: Record<PettyCashCategory, CategoryMeta> = {
  postage: {
    label: "Postage",
    icon: (
      <>
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <path d="m22 6-10 7L2 6" />
      </>
    ),
  },
  conveyance: {
    label: "Conveyance",
    icon: (
      <>
        <path d="M5 17h2M17 17h2" />
        <path d="M3 17V9l2-5h14l2 5v8" />
        <path d="M3 13h18" />
      </>
    ),
  },
  travel: {
    label: "Travel",
    icon: (
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
    ),
  },
  refreshments: {
    label: "Refreshments",
    icon: (
      <>
        <path d="M17 8h1a4 4 0 0 1 0 8h-1" />
        <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" />
        <path d="M6 1v3M10 1v3M14 1v3" />
      </>
    ),
  },
  office_supplies: {
    label: "Office supplies",
    icon: (
      <>
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M2 10h20" />
      </>
    ),
  },
  printing_stationery: {
    label: "Printing",
    icon: (
      <>
        <path d="M6 9V2h12v7" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
      </>
    ),
  },
  repairs_maintenance: {
    label: "Repairs",
    icon: (
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    ),
  },
  sundries: {
    label: "Sundries",
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 2-3 4" />
        <path d="M12 17h.01" />
      </>
    ),
  },
};
